import type { TFunction } from 'i18next';
import { Link, useMatch } from 'react-router';
import { useTranslation } from 'react-i18next';
import { RowActionsMenu, type RowAction } from '@/components/ui/row-actions-menu';
import { formatDate } from '@/lib/intl';
import { cn } from '@/lib/utils';
import { useRoleText } from '../roles/role-text';
import { Avatar, RoleChip, useExpiryText } from '../ui/person-bits';
import type { useAccessActions } from './person-actions';
import type { InvitationRow, PeopleItem, PersonRow } from './use-people';

/** About two chips' worth of names before «+n»: the column must never wrap a row. */
const CHIP_BUDGET = 40;
type Translate = TFunction<'administration'>;

export type Actions = ReturnType<typeof useAccessActions>;

export function PeopleListItem({
  item,
  placeName,
  personHref,
  actions,
  onHide,
  onRestore,
}: {
  readonly item: PeopleItem;
  readonly placeName: string;
  readonly personHref: (id: string) => string;
  readonly actions: Actions;
  readonly onHide: () => void;
  readonly onRestore: () => void;
}) {
  return item.kind === 'user' ? (
    <PersonItem
      person={item}
      placeName={placeName}
      href={personHref(item.id)}
      actions={actions}
      onHide={onHide}
      onRestore={onRestore}
    />
  ) : (
    <InvitationItem row={item} actions={actions} />
  );
}

/** The acts a person's row offers: only those this caller may use on this person (FR-AD-3). */
export function personMenu(
  t: Translate,
  person: PersonRow,
  placeName: string,
  actions: Actions,
  hooks: { hide?: () => void; restore?: () => void } = {},
): RowAction[] {
  const menu: RowAction[] = [];
  if (person.can.remove) {
    menu.push({
      id: 'remove',
      label: t('people.actions.remove', { place: placeName }),
      tone: 'destructive',
      busy: actions.isPending(`remove:${person.id}`),
      onSelect: () => void actions.remove(person, hooks),
    });
  }
  if (person.can.deactivate) {
    menu.push({
      id: 'deactivate',
      label: t('people.actions.deactivate'),
      tone: 'destructive',
      busy: actions.isPending(`deactivate:${person.id}`),
      onSelect: () => void actions.deactivate(person),
    });
  }
  if (person.can.reactivate) {
    menu.push({
      id: 'reactivate',
      label: t('people.actions.reactivate'),
      busy: actions.isPending(`reactivate:${person.id}`),
      onSelect: () => void actions.reactivate(person),
    });
  }
  return menu;
}

/** The role chips that fit the column's budget, in order; the first always shows. */
function chipsWithinBudget<T>(roles: readonly T[], lengthOf: (role: T) => number): T[] {
  const shown: T[] = [];
  let used = 0;
  for (const holding of roles) {
    const length = lengthOf(holding);
    if (shown.length > 0 && used + length > CHIP_BUDGET) {
      break;
    }
    shown.push(holding);
    used += length;
  }
  return shown;
}

function PersonItem({
  person,
  placeName,
  href,
  actions,
  onHide,
  onRestore,
}: {
  readonly person: PersonRow;
  readonly placeName: string;
  readonly href: string;
  readonly actions: Actions;
  readonly onHide: () => void;
  readonly onRestore: () => void;
}) {
  const { t } = useTranslation('administration');
  const { name } = useRoleText();
  const selected = useMatch(href.split('?')[0] ?? href) !== null;
  const deactivated = person.status === 'deactivated';

  const shown = chipsWithinBudget(person.roles, (holding) => name(holding.role).length);
  const hiddenCount = person.roles.length - shown.length;
  const menu = personMenu(t, person, placeName, actions, { hide: onHide, restore: onRestore });

  return (
    <div role="listitem" className={cn('people-row', selected && 'people-row-selected')}>
      <Link
        to={href}
        className="people-row-main"
        aria-current={selected ? 'page' : undefined}
        data-person-link={person.id}
      >
        <Avatar name={person.displayName} muted={deactivated} />
        <span className="flex min-w-0 flex-col">
          <span className="people-row-name">{person.displayName}</span>
          <span className="people-row-sub">{person.email}</span>
        </span>
      </Link>
      <span className="people-row-roles">
        {shown.length === 0 ? (
          <span className="text-muted-foreground text-[13px]">{t('people.noDirectRole')}</span>
        ) : (
          shown.map((holding) => <RoleChip key={holding.assignmentId} holding={holding} />)
        )}
        {hiddenCount > 0 ? (
          <span className="text-muted-foreground text-[12.5px] font-medium">
            {t('people.moreRoles', { count: hiddenCount })}
          </span>
        ) : null}
      </span>
      <span className="people-row-status">
        <span className={cn('status-badge', !deactivated && 'success')}>
          {t(`people.status.${person.status}`)}
        </span>
      </span>
      <span className="people-row-updated">{formatDate(person.updatedAt)}</span>
      <span className="people-row-actions">
        {menu.length > 0 ? (
          <RowActionsMenu
            label={t('people.rowActions', { name: person.displayName })}
            actions={menu}
          />
        ) : null}
      </span>
    </div>
  );
}

function invitationMenu(
  t: Translate,
  invitation: InvitationRow['invitation'],
  actions: Actions,
): RowAction[] {
  const menu: RowAction[] = [];
  if (invitation.can.withdraw) {
    menu.push({
      id: 'withdraw',
      label: t('people.actions.withdraw'),
      tone: 'destructive',
      busy: actions.isPending(`withdraw:${invitation.id}`),
      onSelect: () => void actions.withdraw(invitation),
    });
  }
  if (invitation.can.renew) {
    menu.push({
      id: 'renew',
      label: t('people.actions.renew'),
      busy: actions.isPending(`renew:${invitation.id}`),
      onSelect: () => void actions.renew(invitation),
    });
  }
  return menu;
}

function InvitationItem({
  row,
  actions,
}: {
  readonly row: InvitationRow;
  readonly actions: Actions;
}) {
  const { t } = useTranslation('administration');
  const { name } = useRoleText();
  const expiry = useExpiryText();
  const { invitation } = row;

  const menu = invitationMenu(t, invitation, actions);

  return (
    <div role="listitem" className="people-row">
      <span className="people-row-main">
        <Avatar name={invitation.email} invitation />
        <span className="flex min-w-0 flex-col">
          <span className="people-row-name">{invitation.email}</span>
          <span className="people-row-sub">{invitation.displayName ?? t('people.noName')}</span>
        </span>
      </span>
      <span className="people-row-roles">
        {invitation.roles.map((role) => (
          <span key={role.id} className="role-chip">
            {name(role)}
          </span>
        ))}
      </span>
      <span className="people-row-status">
        <span className="flex flex-col items-start gap-1">
          <span className="status-badge info">{t('people.status.invited')}</span>
          <span className="text-muted-foreground text-[12px]">{expiry(invitation.expiresAt)}</span>
        </span>
      </span>
      <span className="people-row-updated">{formatDate(invitation.createdAt)}</span>
      <span className="people-row-actions">
        {menu.length > 0 ? (
          <RowActionsMenu
            label={t('people.rowActions', { name: invitation.email })}
            actions={menu}
          />
        ) : null}
      </span>
    </div>
  );
}
